// ============================================
// STATE - Venta Store (Borrador local)
// ============================================

import { create } from 'zustand';
import type { ProductoResumen, ClienteResumen, MetodoPago } from '../domain/entities';

interface VentaDetalleLocal {
  producto: ProductoResumen;
  cantidad: number;
  precioUnitario: number;
  subtotal: number;
}

interface VentaState {
  // Borrador actual
  cliente: ClienteResumen | null;
  detalles: VentaDetalleLocal[];
  descuento: number;
  metodoPago: MetodoPago;
  notas: string;

  // Cálculos
  subtotal: number;
  impuestos: number;
  total: number;

  // Actions
  setCliente: (cliente: ClienteResumen | null) => void;
  addProducto: (producto: ProductoResumen, cantidad: number) => void;
  updateCantidad: (productoId: string, cantidad: number) => void;
  removeProducto: (productoId: string) => void;
  setDescuento: (descuento: number) => void;
  setMetodoPago: (metodoPago: MetodoPago) => void;
  setNotas: (notas: string) => void;
  calcularTotales: () => void;
  limpiarVenta: () => void;
  canSubmit: () => boolean;
}

const IGV_RATE = 0.18; // 18%

export const useVentaStore = create<VentaState>((set, get) => ({
  cliente: null,
  detalles: [],
  descuento: 0,
  metodoPago: 'EFECTIVO',
  notas: '',
  subtotal: 0,
  impuestos: 0,
  total: 0,

  setCliente: (cliente) => set({ cliente }),

  addProducto: (producto, cantidad) => {
    const detalles = get().detalles;
    const existente = detalles.find((d) => d.producto.id === producto.id);

    if (existente) {
      // Si ya existe, incrementar cantidad
      set({
        detalles: detalles.map((d) =>
          d.producto.id === producto.id
            ? {
                ...d,
                cantidad: d.cantidad + cantidad,
                subtotal: (d.cantidad + cantidad) * d.precioUnitario,
              }
            : d
        ),
      });
    } else {
      // Agregar nuevo
      set({
        detalles: [
          ...detalles,
          {
            producto,
            cantidad,
            precioUnitario: producto.precioVenta,
            subtotal: cantidad * producto.precioVenta,
          },
        ],
      });
    }

    get().calcularTotales();
  },

  updateCantidad: (productoId, cantidad) => {
    if (cantidad <= 0) {
      get().removeProducto(productoId);
      return;
    }

    set({
      detalles: get().detalles.map((d) =>
        d.producto.id === productoId
          ? {
              ...d,
              cantidad,
              subtotal: cantidad * d.precioUnitario,
            }
          : d
      ),
    });

    get().calcularTotales();
  },

  removeProducto: (productoId) => {
    set({
      detalles: get().detalles.filter((d) => d.producto.id !== productoId),
    });
    get().calcularTotales();
  },

  setDescuento: (descuento) => {
    set({ descuento });
    get().calcularTotales();
  },

  setMetodoPago: (metodoPago) => set({ metodoPago }),

  setNotas: (notas) => set({ notas }),

  calcularTotales: () => {
    const { detalles, descuento } = get();
    
    const subtotal = detalles.reduce((sum, d) => sum + d.subtotal, 0);
    const subtotalConDescuento = subtotal - descuento;
    const impuestos = subtotalConDescuento * IGV_RATE;
    const total = subtotalConDescuento + impuestos;

    set({
      subtotal,
      impuestos,
      total: Math.max(0, total),
    });
  },

  limpiarVenta: () => {
    set({
      cliente: null,
      detalles: [],
      descuento: 0,
      metodoPago: 'EFECTIVO',
      notas: '',
      subtotal: 0,
      impuestos: 0,
      total: 0,
    });
  },

  canSubmit: () => {
    const { detalles, total } = get();
    return detalles.length > 0 && total > 0;
  },
}));
